<template>
  <focus-trap :escapeDeactivates="false" :clickDeactivates="false">
    <div
      class="treatment-medicine-popup"
      @keyup.esc.stop="confirmClose"
      tabindex="-1"
    >
      <validation-observer
        class="validation-observer"
        v-slot="{ invalid }"
        ref="observer"
      >
        <popup
          :invalid="invalid"
          :contentStyle="{}"
          @cancel="confirmClose"
          @click-close-mark="confirmClose"
          @decision="decision"
        >
          <template v-slot:content>
            <div class="treatment-medicine-popup-slot-content">
              <div class="title">{{ title }}</div>
              <treatment-medicine-form-in-popup
                :treatmentItem="treatmentItem"
                :totalPrice="totalPrice"
                :prescriptionDisplayFlg="prescriptionDisplayFlg"
                @click="prescriptionDisplayFlg = true"
              />
              <div class="prescription-area" v-if="prescriptionDisplayFlg">
                <prescription-input-form
                  :treatmentItem="treatmentItem"
                  @click="deletePrescription"
                />
                <div class="prescription-detail">
                  <prescription-detail-input-form
                    :showFlg="prescriptionDetailDisplayFlg"
                    :dailyDose="dailyDose"
                    :singleDose="singleDose"
                    :options="options"
                    :takingTime="takingTime"
                    :oral="oral"
                    :period="period"
                    :periodTypeSelectData="periodTypeSelectData"
                    @click="
                      prescriptionDetailDisplayFlg = !prescriptionDetailDisplayFlg
                    "
                  />
                </div>
              </div>
            </div>
          </template>
        </popup>
      </validation-observer>
      <announce-popup
        v-if="popupFlg"
        v-bind="popup"
        @cancel="popupFlg = false"
        @decision="$emit('cancel')"
        >{{ popup.message }}</announce-popup
      >
    </div>
  </focus-trap>
</template>

<script>
import Popup from '@/components/popups/Popup'
import TreatmentMedicineFormInPopup from '@/components/parts/organisms/TreatmentMedicineFormInPopup'
import PrescriptionInputForm from '@/components/parts/molecules/PrescriptionInputForm'
import PrescriptionDetailInputForm from '@/components/parts/molecules/PrescriptionDetailInputForm'
import AnnouncePopup from '@/components/popups/AnnouncePopup'
import CheckPopupInputDifference from '@/components/mixins/CheckPopupInputDifference'
import { ValidationObserver } from 'vee-validate'
import { FocusTrap } from 'focus-trap-vue'
import { mapGetters } from 'vuex'
import _ from 'lodash'

export default {
  name: 'TreatmentMedicinePopup',

  components: {
    Popup,
    TreatmentMedicineFormInPopup,
    PrescriptionInputForm,
    PrescriptionDetailInputForm,
    AnnouncePopup,
    ValidationObserver,
    FocusTrap
  },

  mixins: [CheckPopupInputDifference],

  props: {
    treatmentItem: { type: Object },
    editFlg: { type: Boolean, default: false }
  },

  data() {
    return {
      prescriptionDisplayFlg: false,
      prescriptionDetailDisplayFlg: true,
      singleDose: {
        text: '',
        selectUnitType: this.treatmentItem?.unitTypeId || 0
      },
      dailyDose: {
        selectValue: 0,
        freeText: ''
      },
      options: [
        { id: 1, eachValue: 1, labelName: '１日１回' },
        { id: 2, eachValue: 2, labelName: '１日２回' },
        { id: 3, eachValue: 3, labelName: '１日３回' },
        { id: 4, eachValue: 4, labelName: '１日４回' },
        { id: 5, eachValue: 5, labelName: '２日に１回' },
        { id: 6, eachValue: 6, labelName: '３日に１回' },
        { id: 7, eachValue: 7, labelName: '４日に１回' },
        { id: 8, eachValue: 8, labelName: '２週に１回' },
        { id: 9, eachValue: 9, labelName: '月に１回' },
        { id: 10, eachValue: 10, labelName: '頓服' },
        { id: 11, eachValue: 11, labelName: '' }
      ],
      takingTime: {
        morningFlg: false,
        noonFlg: false,
        nightFlg: false,
        beforeMealFlg: false,
        afterMealFlg: false,
        betweenMealFlg: false,
        beforeSleepFlg: false,
        useAsNeededFlg: false,
        takingTimeFreeFlg: false,
        takingTimeFree: ''
      },
      oral: {
        oralAdministrationFlg: false,
        subcutaneousAdministrationFlg: false,
        subcutaneousDripFlg: false,
        noseDropsFlg: false,
        earDropsBothFlg: false,
        earDropsLeftFlg: false,
        earDropsRightFlg: false,
        eyesBothFlg: false,
        eyesLeftFlg: false,
        eyesRightFlg: false,
        rectalAdministrationFlg: false,
        intravaginalAdministrationFlg: false,
        oralFreeFlg: false,
        oralFree: ''
      },
      period: {
        text: '',
        selectPeriod: 1
      },
      initialData: {},
      popupFlg: false,
      popup: {
        type: 'alert',
        title: '確認',
        message: '入力内容を保存せずに閉じようとしています。\nよろしいですか？',
        buttons: ['キャンセル', '閉じる'],
        layerNumber: 2
      }
    }
  },

  computed: {
    ...mapGetters({
      getMasterDatum: 'master/getDataById'
    }),
    totalPrice() {
      return (
        parseInt(
          this.treatmentItem.unitPrice *
            this.treatmentItem.amount *
            (1 - this.treatmentItem.discountRate / 100)
        ) - this.treatmentItem.discountPrice
      )
    },
    title() {
      return this.treatmentItem.treatmentOriginalId !== 0
        ? '診療項目 詳細'
        : '薬剤物品 詳細 処方入力'
    },
    periodTypeSelectData() {
      return [
        { id: 1, name: '時間分' },
        { id: 2, name: '日分' },
        { id: 3, name: '週分' },
        { id: 4, name: 'ヶ月分' },
        { id: 5, name: '年分' }
      ]
    },
    diffFlg() {
      return !_.isEqual(this.initialData, this.treatmentItem)
    }
  },

  created() {
    if (this.editFlg && this.treatmentItem.prescription !== '') {
      this.prescriptionDisplayFlg = true
      this.prescriptionDetailDisplayFlg = false
    }
    this.initialData = _.cloneDeep(this.treatmentItem)
  },

  watch: {
    singleDose: {
      deep: true,
      handler: function() {
        this.addText()
      }
    },
    dailyDose: {
      deep: true,
      handler: function() {
        this.addText()
      }
    },
    takingTime: {
      deep: true,
      handler: function() {
        this.addText()
      }
    },
    oral: {
      deep: true,
      handler: function() {
        this.addText()
      }
    },
    period: {
      deep: true,
      handler: function() {
        this.addText()
      }
    }
  },

  methods: {
    decision() {
      this.$emit(
        'decision',
        { ...this.treatmentItem, totalPrice: this.totalPrice },
        this.editFlg
      )
    },
    deletePrescription() {
      this.prescriptionDisplayFlg = false
      this.treatmentItem.prescription = ''
    },
    addText() {
      let addText = ''
      addText += this.addSingleDose()
      addText += this.addDailyDose()
      addText += this.addTakingTime()
      addText += this.addOral()
      addText += this.addPeriod()
      this.treatmentItem.prescription = addText
      this.$nextTick(() => {
        this.$refs.observer.validate()
      })
    },
    addSingleDose() {
      if (this.singleDose.text === '') return ''
      const unitType = this.getMasterDatum(
        'unitTypes',
        this.singleDose.selectUnitType
      )
      const unitTypeName = unitType?.name ?? ''
      return '1回 ' + this.singleDose.text + unitTypeName
    },
    addDailyDose() {
      if (this.dailyDose.selectValue === 0) return ''
      const option = this.options.find(
        v => v.eachValue === this.dailyDose.selectValue
      )
      const name =
        option.eachValue === 11 ? this.dailyDose.freeText : option.labelName
      return ' ' + name
    },
    addTakingTime() {
      let text = ''
      text += this.takingTime.morningFlg ? ' 朝' : ''
      text += this.takingTime.noonFlg ? ' 昼' : ''
      text += this.takingTime.nightFlg ? ' 夜' : ''
      text += this.takingTime.beforeMealFlg ? ' 食前' : ''
      text += this.takingTime.afterMealFlg ? ' 食後' : ''
      text += this.takingTime.betweenMealFlg ? ' 食間' : ''
      text += this.takingTime.beforeSleepFlg ? ' 寝る前' : ''
      text += this.takingTime.useAsNeededFlg ? ' 頓服' : ''
      text += this.takingTime.takingTimeFreeFlg
        ? ' ' + this.takingTime.takingTimeFree
        : ''
      return text
    },
    addOral() {
      let text = ''
      text += this.oral.oralAdministrationFlg ? ' 経口投与' : ''
      text += this.oral.subcutaneousAdministrationFlg ? ' 皮下投与' : ''
      text += this.oral.subcutaneousDripFlg ? ' 皮下点滴' : ''
      text += this.oral.noseDropsFlg ? ' 点鼻' : ''
      text += this.oral.earDropsBothFlg ? ' 点耳(両)' : ''
      text += this.oral.earDropsLeftFlg ? ' 点耳(左)' : ''
      text += this.oral.earDropsRightFlg ? ' 点耳(右)' : ''
      text += this.oral.eyesBothFlg ? ' 眼(両)' : ''
      text += this.oral.eyesLeftFlg ? ' 眼(左)' : ''
      text += this.oral.eyesRightFlg ? ' 眼(右）' : ''
      text += this.oral.rectalAdministrationFlg ? ' 直腸内投与' : ''
      text += this.oral.intravaginalAdministrationFlg ? ' 膣内投与' : ''
      text += this.oral.oralFreeFlg ? ' ' + this.oral.oralFree : ''
      return text
    },
    addPeriod() {
      if (this.period.text === '') return ''
      const periodTypeName = this.periodTypeSelectData.find(
        v => v.id === this.period.selectPeriod
      ).name
      return ' ' + this.period.text + periodTypeName
    },
    confirmClose() {
      this.diffFlg ? (this.popupFlg = true) : this.$emit('cancel')
    }
  }
}
</script>

<style lang="scss" scoped>
.treatment-medicine-popup {
  position: absolute;
  top: 0;
  left: 0;
  .treatment-medicine-popup-slot-content {
    margin-bottom: 20px;
    width: 740px;
    > .title {
      font-size: 16px;
      font-weight: bold;
    }
    > .prescription-area {
      > .prescription-detail {
        margin-top: 5px;
      }
    }
  }
}
</style>
